<template>
  <div class="tml-returns-and-exchanges">
    <component :is="mobile ? 'h3' : 'h1'" class="mb-4 mt-2">
      <b>
        {{ $route.meta.title }}
      </b>
    </component>
    <tml-loader
      :loading="submitting || !order"
      :class="mobile ? 'pt-1' : 'pt-2'"
      height="400px"
    >
      <div v-if="order" class="description">
        <div v-if="showConfirmationPage">
          <p class="text-lg mt-2 text-primary">
            Please review the items you wish to return
          </p>
          <div v-if="hasRefunds">
            <component :is="mobile ? 'h4' : 'h3'" class="font-bold mt-5 mb-2">
              Refunds
            </component>
            <div class="mt-3">
              <refund-item
                v-for="(refundItem, index) in formattedRefundItems"
                :key="index"
                :refund-item="refundItem"
              />
            </div>
          </div>
          <div v-if="hasExchanges">
            <component :is="mobile ? 'h4' : 'h3'" class="font-bold mt-5 mb-2">
              Exchanges
            </component>
            <div class="mt-4">
              <exchange-item
                v-for="(exchangeItem, index) in formattedExchangeItems"
                :key="index"
                class="mb-5"
                :exchange-item="exchangeItem"
              />
            </div>
          </div>
          <div class="pt-2 flex">
            <tml-button
              fill
              text="Cancel"
              class="mr-3"
              :style="mobile ? 'width:50%;' : 'width:30%;'"
              @click="resetData"
            />
            <tml-button
              fill
              primary
              text="Confirm"
              :style="mobile ? 'width:50%;' : 'width:30%;'"
              @click="submitReturn"
            />
          </div>
        </div>
        <div v-if="immediate && submitted">
          We've successfully updated your order.
        </div>
        <div v-if="!immediate && submitted">
          We've sent a returns label to
          <b>{{ order.billingEmail }}</b> or you can download it using the
          button below. Just print it off and use it to send your items back to
          us.
          <div>
            <tml-button
              :download="getReturnsLabelFilename()"
              :href="getReturnLabelUrl()"
              :small="mobile"
              class="my-4"
              text="Download return label"
            />
          </div>
          <returns-page-questions class="mt-6" />
        </div>
        <order-status
          v-if="!submitted"
          :order="order"
          :order-hash="orderHash"
          :faulty-only="faultyOnly"
          :show-confirmation-page="showConfirmationPage"
        />
      </div>
    </tml-loader>
  </div>
  <tml-grid
    v-if="
      order &&
        order.items &&
        order.items.length &&
        !showConfirmationPage &&
        !submitted
    "
    class="order-items pt-3"
    :breakpoints="{xs: 1}"
  >
    <order-item-return
      v-for="item in order.items"
      :key="item.id"
      :return-reasons="getItemReturnReasons(item)"
      :available-options="availableOptions[item.product.id]"
      :force-reason="order.hasBeenIn('complete')"
      :order="order"
      :item="item"
      @remove-item="removeItem"
      @add-item="addItem"
    />
  </tml-grid>
  <template
    v-if="order && hasReturnItems && !showConfirmationPage && !submitted"
  >
    <p v-if="!order.hasBeenIn('complete')" class="center mb-5">
      Please review your changes and make sure they're correct. Any changes you
      make will <b>NOT</b> be able to be undone.
    </p>
    <tml-item-tray>
      <template #primary>
        <tml-button
          fill
          primary
          text="Next"
          @click="() => (showConfirmationPage = true)"
        />
      </template>
    </tml-item-tray>
  </template>
</template>

<script>
import {PolicyViewer} from '@teemill/modules/policy';
import {Order} from '@teemill/common/models';

import {formatUrl, maxDevice} from '@teemill/common/helpers';
import {computed} from 'vue';
import {useHead} from '@vueuse/head';

import OrderItemReturn from './OrderItemReturn.vue';
import RefundItem from './RefundItem.vue';
import ExchangeItem from './ExchangeItem.vue';
import ReturnsPageQuestions from './ReturnsPageQuestions.vue';
import OrderStatus from './OrderStatus.vue';

export default {
  name: 'ReturnsPage',

  components: {
    PolicyViewer,
    OrderItemReturn,
    RefundItem,
    ExchangeItem,
    ReturnsPageQuestions,
    OrderStatus,
  },

  data() {
    return {
      order: null,

      immediate: false,
      showConfirmationPage: false,

      submitted: false,
      submitting: false,

      quantity: null,

      reason: null,

      returnReasons: [],
      refundItems: [],
      exchangeItems: [],
      loading: false,
      availableOptions: {},

      faultyOnly: null,
    };
  },

  computed: {
    mobile() {
      return maxDevice('md');
    },

    orderHash() {
      return this.$route.params.orderHash;
    },

    returnItems() {
      return this.refundItems.concat(this.exchangeItems);
    },

    hasRefunds() {
      return this.refundItems?.length > 0;
    },

    hasExchanges() {
      return this.exchangeItems?.length > 0;
    },

    hasReturnItems() {
      return this.returnItems && this.returnItems.length > 0;
    },

    formattedExchangeItems() {
      const {items} = this.order;

      return this.exchangeItems.map(exchangeItem => {
        const matchingItem = items.find(item => item.id === exchangeItem.id);
        if (matchingItem) {
          const updatedItem = this.constructItemFromMatch(
            matchingItem,
            exchangeItem
          );
          updatedItem.exchangeFor = {
            ...exchangeItem.exchangeFor,
            product: {...matchingItem.product},
          };
          return this.mapOptionsToCamelCase(updatedItem);
        }
        return exchangeItem;
      });
    },

    formattedRefundItems() {
      const {items} = this.order;

      if (this.refundItems.length) {
        return this.refundItems.map(refundItem => {
          const matchingItem = items.find(item => item.id === refundItem.id);
          if (matchingItem) {
            const updatedItem = this.constructItemFromMatch(
              matchingItem,
              refundItem
            );
            return this.mapOptionsToCamelCase(updatedItem);
          }
          return refundItem;
        });
      }
      return [];
    },

    submittedExchangeItems() {
      return this.exchangeItems.map(
        ({
          id,
          returnType,
          reason,
          optionMatrixRecordId,
          quantity,
          exchangeFor,
        }) => {
          const {colour, size} = exchangeFor.options;

          return {
            id,
            returnType,
            reason,
            optionMatrixRecordId,
            quantity,
            exchangeFor: {colour, size},
          };
        }
      );
    },

    submittedRefundItems() {
      return this.refundItems.map(
        ({
          id,
          returnType,
          reason,
          optionMatrixRecordId,
          quantity,
          exchangeFor,
        }) => {
          const {colour} = exchangeFor.options;

          return {
            id,
            returnType,
            reason,
            optionMatrixRecordId,
            quantity,
            exchangeFor: {colour},
          };
        }
      );
    },
  },

  mounted() {
    if (this.orderHash) {
      this.findOrder();
    } else {
      this.$router.push({
        path: '/help/question/how-do-i-return-my-order/',
      });
    }
  },

  created() {
    useHead(
      computed(() => ({
        title: $store.state.title,
        meta: [{name: 'robots', content: 'noindex'}],
      }))
    );
  },

  methods: {
    findOrder() {
      this.loading = true;
      this.axios
        .get(formatUrl(`/omnis/v3/frontend/retex/order/get/${this.orderHash}`))
        .success(data => {
          if (data) {
            this.order = Order.map(data.order);

            this.faultyOnly = data.faultyOnly;

            data.order.items.forEach(item => {
              this.availableOptions[item.product.id] =
                item.product.available_options;
            });

            this.returnReasons = data.returnReasons;
          }
        })
        .oops()
        .finally(() => {
          this.loading = false;
        });
    },

    submitReturn() {
      this.submitting = true;

      this.axios
        .post(formatUrl('/omnis/v3/frontend/retex/submit/'), {
          orderHash: this.orderHash,
          refundItems: this.submittedRefundItems,
          exchangeItems: this.submittedExchangeItems,
        })
        .success(data => {
          if (data && data.immediate) {
            this.immediate = true;
          }
          this.submitted = true;
          this.showConfirmationPage = false;
          window.scrollTo(0, 0);

          this.exchangeItems = [];
          this.refundItems = [];
        })
        .oops()
        .finally(() => {
          this.submitting = false;
        });
    },

    addItem(item) {
      this.removeItem(item);
      this[`${item.returnType}Items`].push(item);
    },

    removeItem(item) {
      this.refundItems = this.refundItems.filter(
        refundItem => refundItem.id !== item.id
      );

      this.exchangeItems = this.exchangeItems.filter(
        exchangeItem => exchangeItem.id !== item.id
      );
    },

    resetData() {
      this.immediate = false;
      this.showConfirmationPage = false;
      this.submitted = false;
      this.refundItems = [];
      this.exchangeItems = [];
    },

    getItemReturnReasons(item) {
      let reasons = this.returnReasons;

      if (this.faultyOnly || item.product.isCustomProduct(true)) {
        reasons = reasons.filter(reason => reason.faulty);
      }

      return reasons;
    },

    mapOptionsToCamelCase(item) {
      const camelCaseOptions = {
        ...item,
        options: {
          size: item.options.Size,
          colour: item.options.Colour,
        },
      };
      return camelCaseOptions;
    },
    getReturnLabelUrl() {
      return formatUrl(
        `/omnis/v3/frontend/retex/returns-label/${this.orderHash}/`
      );
    },
    getReturnsLabelFilename() {
      return `${
        this.order.division ? `${this.order.division.name.toLowerCase()}-` : ''
      }${this.labelType ? `${this.labelType}-` : ''}returns-label.pdf`;
    },
    constructItemFromMatch(matchingItem, baseItem) {
      const {product, images, options} = matchingItem;
      return {
        ...baseItem,
        product: {...product},
        images: {...images.sizes},
        options: {...options},
      };
    },
    formatUrl,
  },
};
</script>
